<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :styleConfig="styleConfig"
    :style="style.root"
  >
    <div
      :disabled="disabled"
      :class="$style.container"
      :style="style.container"
      @dragover.prevent
      @drop.prevent="selectFileByDrop"
    >
      <input
        :accept="accept"
        :disabled="disabled"
        :id="`form${_uid}`"
        type="file"
        @click="onClick"
        @change="selectFileByClick"
        :style="{ display: 'none' }"
      />
      <label
        :disabled="disabled"
        :for="`form${_uid}`"
        :class="$style.label"
        :style="style.label"
      >
        <slot />
      </label>
    </div>
  </div>
</template>

<script>
  import { CssStyleProcessor } from '@js/vue-style-utils'
  import { isFile } from '@js/data-utils'

  export default {
    name: 'BaseFilePicker',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change-file',
    },
    props: {
      vmodelValue: {
        type: File,
        required: false,
        default: null,
      },
      acceptedExtensions: {
        type: Array,
        required: false,
        default: () => [],
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      disableClick: {
        type: Boolean,
        required: false,
        default: false,
      },
      disableDrop: {
        type: Boolean,
        required: false,
        default: false,
      },
      maxFileSize: {
        type: Number,
        required: false,
        default: 0, // By default no limit. Specify size in bytes.
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        file: null,
        style: {
          container: {},
          label: {},
          root: {},
        },
      };
    },
    created: function () {
      if (this.vmodelValue !== null) {
        this.setFile(this.vmodelValue, true);
      }

      const proc = new CssStyleProcessor('--v2-base-file-picker');

      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
      Object.assign(
        this.style.label,
        proc.getExtendedStyle(this.styleConfig.label, 'label'),
      );
      Object.assign(
        this.style.root,
        this.styleConfig.root,
      );
    },
    computed: {
      accept: function () {
        return this.acceptedExtensions
          .map(ext => ext.startsWith('.') ? ext : '.' + ext)
          .join(', ');
      },
    },
    methods: {
      checkFileExtension: function (file) {
        const extension = this.getFileExtension(file);
        return extension !== null;
      },
      checkFileSize: function (file) {
        return this.maxFileSize
          ? file.size <= this.maxFileSize
          : true;
      },
      getFileExtension: function (file) {
        return isFile(file)
          ? (file.name.includes('.') ? file.name.split('.').pop() : null)
          : null;
      },
      onClick: function (e) {
        if (this.disableClick) {
          e.preventDefault();
        }
      },
      selectFileByClick: function (e) {
        if (this.disableClick) {
          return;
        }
        this.setFile(e.target.files[0], false);
      },
      selectFileByDrop: function (e) {
        if (this.disableDrop) {
          return;
        }
        this.setFile(e.dataTransfer.files[0], false);
      },
      setFile: function (file, setNull = false) {
        let errorMessage = '';

        if (!isFile(file)) {
          errorMessage = 'Invalid file object';
        } else if (!this.checkFileExtension(file)) {
          errorMessage = 'Invalid file type';
        } else if (!this.checkFileSize(file)) {
          errorMessage = 'File too large';
        }

        if (errorMessage.length > 0) {
          this.$store.commit('notification/setWarning', errorMessage);
          this.file = setNull ? null : this.file;
        } else {
          this.file = file;
        }

        this.$emit('change-file', this.file);
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.setFile(newVal, true);
      },
    },
  };
</script>

<style lang="stylus" module>
  .container
    background-color var(--v2-base-file-picker-container-background-color)
    border var(--v2-base-file-picker-container-border)
    border-radius var(--v2-base-file-picker-container-border-radius)
    border-width var(--v2-base-file-picker-container-border-width)
    box-shadow var(--v2-base-file-picker-container-box-shadow)
    cursor var(--v2-base-file-picker-container-cursor)
    &:hover
      background-color var(--v2-base-file-picker-container-background-color-hover)
      border var(--v2-base-file-picker-container-border-hover)
      border-radius var(--v2-base-file-picker-container-border-radius-hover)
      border-width var(--v2-base-file-picker-container-border-width-hover)
      box-shadow var(--v2-base-file-picker-container-box-shadow-hover)
      cursor var(--v2-base-file-picker-container-cursor-hover)
    &:active
      background-color var(--v2-base-file-picker-container-background-color-active)
      border var(--v2-base-file-picker-container-border-active)
      border-radius var(--v2-base-file-picker-container-border-radius-active)
      border-width var(--v2-base-file-picker-container-border-width-active)
      box-shadow var(--v2-base-file-picker-container-box-shadow-active)
      cursor var(--v2-base-file-picker-container-cursor-active)
    &[disabled]
      background-color var(--v2-base-file-picker-container-background-color-disabled)
      border var(--v2-base-file-picker-container-border-disabled)
      border-radius var(--v2-base-file-picker-container-border-radius-disabled)
      border-width var(--v2-base-file-picker-container-border-width-disabled)
      box-shadow var(--v2-base-file-picker-container-box-shadow-disabled)
      cursor var(--v2-base-file-picker-container-cursor-disabled)

  .label
    background-color var(--v2-base-file-picker-label-background-color)
    border var(--v2-base-file-picker-label-border)
    border-radius var(--v2-base-file-picker-label-border-radius)
    border-width var(--v2-base-file-picker-label-border-width)
    box-shadow var(--v2-base-file-picker-label-box-shadow)
    color var(--v2-base-file-picker-label-color)
    cursor var(--v2-base-file-picker-label-cursor)
    font-family var(--v2-base-file-picker-label-font-family)
    font-size var(--v2-base-file-picker-label-font-size)
    font-weight var(--v2-base-file-picker-label-font-weight)
    &:hover
      background-color var(--v2-base-file-picker-label-background-color-hover)
      border var(--v2-base-file-picker-label-border-hover)
      border-radius var(--v2-base-file-picker-label-border-radius-hover)
      border-width var(--v2-base-file-picker-label-border-width-hover)
      box-shadow var(--v2-base-file-picker-label-box-shadow-hover)
      color var(--v2-base-file-picker-label-color-hover)
      cursor var(--v2-base-file-picker-label-cursor-hover)
      font-family var(--v2-base-file-picker-label-font-family-hover)
      font-size var(--v2-base-file-picker-label-font-size-hover)
      font-weight var(--v2-base-file-picker-label-font-weight-hover)
    &:active
      background-color var(--v2-base-file-picker-label-background-color-active)
      border var(--v2-base-file-picker-label-border-active)
      border-radius var(--v2-base-file-picker-label-border-radius-active)
      border-width var(--v2-base-file-picker-label-border-width-active)
      box-shadow var(--v2-base-file-picker-label-box-shadow-active)
      color var(--v2-base-file-picker-label-color-active)
      cursor var(--v2-base-file-picker-label-cursor-active)
      font-family var(--v2-base-file-picker-label-font-family-active)
      font-size var(--v2-base-file-picker-label-font-size-active)
      font-weight var(--v2-base-file-picker-label-font-weight-active)
    &[disabled]
      background-color var(--v2-base-file-picker-label-background-color-disabled)
      border var(--v2-base-file-picker-label-border-disabled)
      border-radius var(--v2-base-file-picker-label-border-radius-disabled)
      border-width var(--v2-base-file-picker-label-border-width-disabled)
      box-shadow var(--v2-base-file-picker-label-box-shadow-disabled)
      color var(--v2-base-file-picker-label-color-disabled)
      cursor var(--v2-base-file-picker-label-cursor-disabled)
      font-family var(--v2-base-file-picker-label-font-family-disabled)
      font-size var(--v2-base-file-picker-label-font-size-disabled)
      font-weight var(--v2-base-file-picker-label-font-weight-disabled)
</style>
